import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CheckIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, LinkButton } from '../../components/ui/Button';
import { EnquiryImages } from '../../components/admin/EnquiryImages';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDateTime, telHref } from '../../utils/format';
import type { Enquiry } from '../../types/admin';
const ease = [0.23, 1, 0.32, 1] as const;
const statusStyles: Record<string, string> = {
  'Ny henvendelse': 'border-transparent bg-accent text-white',
  Accepteret: 'border-status-ok/35 bg-status-ok/10 text-status-ok',
  Behandlet: 'border-line bg-canvas text-ink-soft',
  Afvist: 'border-danger/30 bg-danger/10 text-danger'
};
export function Enquiries() {
  const {
    enquiries,
    dataLoading,
    createCaseFromEnquiry,
    createQuoteFromEnquiry,
    acceptEnquiry,
    deleteEnquiry
  } = useAdminData();
  const navigate = useNavigate();
  const [working, setWorking] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Enquiry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [actionError, setActionError] = useState('');
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1295" data-mp="3fykvy5e15c9g">
      <p className="text-sm text-ink-muted" data-id="element-1296" data-mp="3tx2i3yoaegp9">Fra formularen “Fortæl om din opgave”</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1297" data-mp="3tupoupd2wjww">
        Henvendelser
      </h1>

      {accepted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-1298" data-mp="1958rkhjg0rlq">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1299" data-mp="1v3w4z8azbk6">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1300" data-mp="6rubtlg74552" />
          </span>
          Henvendelsen er accepteret
        </p>}

      {actionError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-1301" data-mp="fulplrwjnczv">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1302" data-mp="3fb1v39n3rru3" />
          {actionError}
        </p>}

      {deleted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-1303" data-mp="3o9di71org7hs">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1304" data-mp="3t2ggto8ea74u">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1305" data-mp="30k2rye51dvhw" />
          </span>
          Henvendelsen er slettet
        </p>}

      {dataLoading && enquiries.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1306" data-mp="2o1p8ls1s7v1g">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1307" data-mp="2nzm7ocz3chhy" />
          Henter henvendelser …
        </p>}

      <ul className="mt-7 grid gap-5" data-id="element-1308" data-mp="2f1akym1ci3us">
        {enquiries.map(enquiry => <li key={enquiry.id} className="rounded-card border border-line bg-white p-5 md:p-6" data-id="element-1309" data-mp="2f3d88yb45ivt">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1310" data-mp="2a3sb8tfi7klq">
              <div data-id="element-1311" data-mp="259wetvew25so">
                <p className="text-xs text-ink-muted" data-id="element-1312" data-mp="259e8u72fcd7k">{formatDateTime(enquiry.createdAt) || 'Netop nu'}</p>
                <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink" data-id="element-1313" data-mp="1waj5r3uofj8h">{enquiry.name}</h2>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[enquiry.status] ?? 'border-line bg-canvas text-ink-soft'}`} data-id="element-1314" data-mp="1rbpr383nlm6q">
                {enquiry.status}
              </span>
            </div>

            <dl className="mt-4 grid gap-3 text-[15px] sm:grid-cols-3" data-id="element-1315" data-mp="3rdz1uxyexsqq">
              <div data-id="element-1316" data-mp="3cot6jl2p4mo4">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-1317" data-mp="3cpl06qcqgabr">Telefon</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-1318" data-mp="3hncprdpwnkcs">
                  <PhoneIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-1319" data-mp="3hljlg52rlc7y" />
                  {enquiry.phone || '—'}
                </dd>
              </div>
              <div data-id="element-1320" data-mp="37r9osb6shrrf">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-1321" data-mp="37nlw1e9659dr">E-mail</dt>
                <dd className="mt-1 flex items-center gap-1.5 break-all text-ink" data-id="element-1322" data-mp="2trrzlupl0chj">
                  <MailIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1323" data-mp="2ypjyj8on8yv8" />
                  {enquiry.email || '—'}
                </dd>
              </div>
              <div data-id="element-1324" data-mp="wxx5z2p3svwn">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-1325" data-mp="wwc750vraqsz">Adresse / område</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-1326" data-mp="2hg7mson4m9v6">
                  <MapPinIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-1327" data-mp="2cheagzjksowl" />
                  {enquiry.address || '—'}
                </dd>
              </div>
            </dl>

            <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-[15px] leading-relaxed text-ink-soft" data-id="element-1328" data-mp="2me969aogsb76">
              {enquiry.description}
            </p>

            <EnquiryImages images={enquiry.images} data-id="element-1329" data-mp="3080hqolra55j" />

            <div className="mt-5 flex flex-wrap gap-3" data-id="element-1330" data-mp="2v6b2ewri35bn">
              {enquiry.status === 'Ny henvendelse' && <Button size="sm" disabled={accepting === enquiry.id} onClick={async () => {
            if (accepting) return;
            setAccepting(enquiry.id);
            setActionError('');
            setDeleted(false);
            const result = await acceptEnquiry(enquiry.id);
            setAccepting(null);
            if (!result.ok) {
              setActionError(result.error ?? 'Henvendelsen kunne ikke accepteres. Prøv igen.');
              return;
            }
            setAccepted(true);
          }} data-id="element-1331" data-mp="3a3v6m1gkowgr">
                  {accepting === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1332" data-mp="2jf29qry2j25w" />
                      Accepterer …
                    </> : 'Accepter henvendelse'}
                </Button>}

              <Button variant="secondary" size="sm" disabled={working === `quote-${enquiry.id}`} onClick={async () => {
            setWorking(`quote-${enquiry.id}`);
            setActionError('');
            const created = await createQuoteFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Tilbuddet kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/tilbud/${created.id}`);
          }} data-id="element-1333" data-mp="2x5ntsjz9di5d">
                {working === `quote-${enquiry.id}` ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1334" data-mp="1e7iwee67u5n3" />
                    Opretter …
                  </> : 'Opret tilbud'}
              </Button>

              {enquiry.caseId ? <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/sager/${enquiry.caseId}`)} data-id="element-1335" data-mp="14etmvou871ue">
                  Åbn sagen
                </Button> : <Button variant={enquiry.status === 'Ny henvendelse' ? 'secondary' : 'primary'} size="sm" disabled={working === enquiry.id} onClick={async () => {
            setWorking(enquiry.id);
            setActionError('');
            const created = await createCaseFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Sagen kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/sager/${created.id}`);
          }} data-id="element-1336" data-mp="1wys9syj3xejy">
                  {working === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1337" data-mp="1a7xqi207wza1" />
                      Opretter …
                    </> : 'Opret sag'}
                </Button>}
              {enquiry.phone && <LinkButton href={telHref(enquiry.phone)} variant="secondary" size="sm" data-id="element-1338" data-mp="3t6ry4ykhqj6">
                  Ring
                </LinkButton>}
              {enquiry.email && <LinkButton href={`mailto:${enquiry.email}`} variant="secondary" size="sm" data-id="element-1339" data-mp="3ngi6vnoqelng">
                  Send e-mail
                </LinkButton>}
              <button type="button" onClick={() => {
            setConfirming(enquiry);
            setDeleteError('');
            setDeleted(false);
            setAccepted(false);
            setActionError('');
          }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-1340" data-mp="57pyni3lx3ik">
                Slet henvendelse
              </button>
            </div>
          </li>)}
      </ul>

      {confirming && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-titel" data-id="element-1341" data-mp="2l06w3wqzkpb2">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1342" data-mp="j2xwvewzdg2h">
            <h2 id="slet-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1343" data-mp="a3klggpaqhf4">
              Er du sikker på, at du vil slette denne henvendelse?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1344" data-mp="9bwu43dz8m8">
              Henvendelsen fra {confirming.name} og dens billeder slettes permanent.
            </p>

            {deleteError && <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-1345" data-mp="3mkg5vpohot4h">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1346" data-mp="3dj9mn8ugh47j" />
                {deleteError}
              </p>}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1347" data-mp="1xyz9g5hgsbmt">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setDeleteError('');
            const result = await deleteEnquiry(confirming);
            setDeleting(false);
            if (!result.ok) {
              setDeleteError(result.error ?? 'Henvendelsen kunne ikke slettes. Prøv igen.');
              return;
            }
            setConfirming(null);
            setDeleted(true);
          }} data-id="element-1348" data-mp="1xx63kseodvfv">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1349" data-mp="qkw41crowf2j" />
                    Sletter …
                  </> : 'Slet henvendelse'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirming(null)} data-id="element-1350" data-mp="1twcq88oqk52t">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}

      {!dataLoading && enquiries.length === 0 && <p className="mt-8 rounded-card border border-dashed border-line bg-white p-10 text-center text-sm text-ink-muted" data-id="element-1351" data-mp="23mo3wmy81smw">
          Der er ingen henvendelser endnu.
        </p>}
    </div>;
}